import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { product as brandProduct } from "../../branding.js";
import { phoneSections, shortTitleOf } from "../../lib/phone/phoneModel.js";
import { switchToDesktopLayout } from "../../hooks/useIsPhone.js";
import { Button, IconButton } from "../ui/Primitives.jsx";
import { IconClose, IconForecasts, IconList, IconMonitor } from "../ui/Icons.jsx";
import { PHONE_CSS } from "./phoneCss.js";
import PhoneForecast from "./PhoneForecast.jsx";
import PhoneForecastList, { PhoneListSkeleton } from "./PhoneForecastList.jsx";

// The read-only phone layout. People often open an alert email on a phone first, so a phone
// shows what a forecast says and why, and opens the full report; asking, updating and
// exploring scenarios stay on the desktop layout. Pure props: everything it opens is a
// handler from AppInner.

/** The bottom sheet that holds the forecast list over a forecast. */
function ListSheet({ open, onClose, children }) {
  const sheetRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    sheetRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    // telemetry-gesture: phone.list-close
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="sy-ph__sheetback" data-ui="phone.list-dismiss" onClick={onClose} />
      <div
        ref={sheetRef}
        className="sy-ph__sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Forecasts"
        tabIndex={-1}
        data-surface="phone.forecast-list"
      >
        <div className="sy-ph__grab" aria-hidden="true" />
        <div className="sy-ph__sheethead">
          <h2>Forecasts</h2>
          <IconButton
            label="Close"
            className="sy-ph__close"
            data-ui="phone.list-close"
            onClick={onClose}
          >
            <IconClose />
          </IconButton>
        </div>
        <div className="sy-ph__sheetbody">{children}</div>
      </div>
    </>
  );
}

function DesktopLink() {
  return (
    <button
      type="button"
      className="sy-ph__desk"
      data-ui="phone.use-desktop"
      onClick={switchToDesktopLayout}
    >
      <IconMonitor />
      Use the desktop layout
    </button>
  );
}

function Footer() {
  return (
    <footer className="sy-ph__foot">
      <span>Asking new questions and exploring scenarios is on a computer.</span>
      <DesktopLink />
    </footer>
  );
}

function EmptyState() {
  return (
    <div className="sy-ph__empty">
      <div className="sy-ph__emptyicon" aria-hidden="true">
        <IconForecasts />
      </div>
      <h1>No forecasts yet</h1>
      <p>
        Asking {brandProduct.prose} a new question happens on a computer. Each forecast you ask for
        shows up here, ready to read on your phone.
      </p>
      <Button data-ui="phone.use-desktop" icon={<IconMonitor />} onClick={switchToDesktopLayout}>
        Use the desktop layout
      </Button>
    </div>
  );
}

/**
 * @param {object} props
 * @param {any[]} props.forecasts the owner's forecasts (watchlist rows merged in)
 * @param {any[]} [props.sharedForecasts] forecasts shared with the viewer
 * @param {string[]|null} [props.forecastOrder] the viewer's own ordering
 * @param {(f: any) => boolean} props.isWatchlisted
 * @param {boolean} props.loaded the forecast list has loaded
 * @param {any|null} props.active the forecast on screen
 * @param {object|null} [props.previous] the version to compare it with; undefined derives it
 *   from `active`
 * @param {number} props.nowMs the app's "today"
 * @param {string|null} [props.updatedAt] the active forecast's latest forecast moment
 * @param {boolean} props.concluded the active question has settled
 * @param {object|null} [props.triggerResolutions] graded trigger outcomes for the active forecast
 * @param {boolean} props.reportEnabled
 * @param {(f: any) => void} props.onOpenForecast
 * @param {() => void} props.onOpenReport
 */
export default function PhoneApp({
  forecasts,
  sharedForecasts = [],
  forecastOrder = null,
  isWatchlisted,
  loaded,
  active,
  previous,
  nowMs,
  updatedAt = null,
  concluded = false,
  triggerResolutions = null,
  reportEnabled,
  onOpenForecast,
  onOpenReport,
}) {
  const [listOpen, setListOpen] = useState(false);
  const closeList = useCallback(() => setListOpen(false), []);
  const [scrolled, setScrolled] = useState(false);
  const scrollRef = useRef(null);
  const headingRef = useRef(null);
  const sections = useMemo(
    () =>
      phoneSections({
        forecasts,
        shared: sharedForecasts,
        order: forecastOrder,
        isWatchlisted,
      }),
    [forecasts, sharedForecasts, forecastOrder, isWatchlisted],
  );
  const count = sections.watchlist.length + sections.recent.length;
  const alerts = [...sections.watchlist, ...sections.recent].filter((f) => f?.alert).length;
  const activeId = active?.id ?? null;
  const showForecast = !!activeId;

  // A newly opened forecast starts at its question (the scroll event resets the bar title).
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeId]);

  // The bar names the page once its heading has scrolled under it.
  const onScroll = () => {
    const el = scrollRef.current;
    const h = headingRef.current;
    if (!el) return;
    const past = h
      ? h.getBoundingClientRect().bottom < el.getBoundingClientRect().top + 4
      : el.scrollTop > 40;
    if (past !== scrolled) setScrolled(past);
  };

  const open = (f) => {
    setListOpen(false);
    if (f) onOpenForecast(f);
  };

  const barTitle = showForecast ? shortTitleOf(active) : "Forecasts";
  const list = loaded ? (
    count ? (
      <PhoneForecastList sections={sections} activeId={activeId} nowMs={nowMs} onOpen={open} />
    ) : null
  ) : (
    <PhoneListSkeleton />
  );

  return (
    <div className="sy-ph" data-surface="phone">
      <style>{PHONE_CSS}</style>
      <header className="sy-ph__bar" data-scrolled={scrolled ? "" : undefined}>
        <div className="sy-ph__mark" role="img" aria-label={brandProduct.prose} />
        <div className="sy-ph__stack">
          <span className="sy-ph__wordmark" aria-hidden={scrolled}>
            {brandProduct.prose}
          </span>
          <span className="sy-ph__bartitle" aria-hidden={!scrolled} data-telemetry-redact="">
            {barTitle}
          </span>
        </div>
        {showForecast && count > 0 && (
          <button
            type="button"
            className="sy-ph__listbtn"
            data-ui="phone.open-list"
            aria-haspopup="dialog"
            aria-expanded={listOpen}
            onClick={() => setListOpen(true)}
          >
            <IconList />
            Forecasts
            {alerts > 0 && <span className="sy-ph__alertdot" aria-label={`${alerts} moved`} />}
          </button>
        )}
      </header>

      <div className="sy-ph__scroll" ref={scrollRef} onScroll={onScroll}>
        <main className="sy-ph__main">
          {showForecast ? (
            <PhoneForecast
              forecast={active}
              previous={previous}
              nowMs={nowMs}
              updatedAt={updatedAt}
              concluded={concluded}
              resolutions={triggerResolutions}
              reportEnabled={reportEnabled}
              onOpenReport={onOpenReport}
              headingRef={(el) => {
                headingRef.current = el;
              }}
            />
          ) : loaded && count === 0 ? (
            <EmptyState />
          ) : (
            <div data-surface="phone.forecasts">
              <div className="sy-ph__pagehead">
                <h1
                  ref={(el) => {
                    headingRef.current = el;
                  }}
                >
                  Forecasts
                </h1>
                <p className="sy-ph__sub">Pick one to read it.</p>
              </div>
              {list}
            </div>
          )}
          {(showForecast || count > 0) && <Footer />}
        </main>
      </div>

      <ListSheet open={listOpen} onClose={closeList}>
        {list}
      </ListSheet>
    </div>
  );
}
